import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { 
  CheckCircle2, 
  Clock, 
  FileText, 
  PenTool, 
  Download,
  AlertCircle,
  User,
  Folder,
  File,
  Settings,
  Image,
  Loader2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { jsPDF } from 'jspdf';
import { toast } from 'sonner';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';

// Dictionnaire de traduction des champs du brief (anglais -> français)
const FIELD_LABELS_FR = {
  // Général
  'description': 'Description du projet',
  'project_type': 'Type de projet',
  'has_existing_site': 'Site existant ?',
  'existing_site_url': 'URL du site existant',
  'main_objective': 'Objectif principal',
  'target_audience': 'Cible / Audience',
  'inspiration_links': 'Liens d\'inspiration',
  
  // Technique
  'needs_hosting': 'Hébergement nécessaire ?',
  'needs_domain': 'Nom de domaine nécessaire ?',
  'site_structure': 'Arborescence du site',
  
  // Vidéo
  'video_type': 'Type de vidéo',
  'video_duration': 'Durée souhaitée',
  'video_format': 'Format(s) souhaité(s)',
  'platforms': 'Plateformes de diffusion',
  'voiceover': 'Voix off',
  'subtitles': 'Sous-titres',
  'sound_recording': 'Prise de son',
  'post_production': 'Post-production',
  'cameras_count': 'Nombre de caméras',
  'shooting_duration': 'Durée du tournage',
  'shooting_location': 'Lieu(x) de tournage',
  
  // Logo
  'brand_values': 'Valeurs de la marque',
  'brand_personality': 'Personnalité de la marque',
  'color_preferences': 'Préférences de couleurs',
  'style_preferences': 'Préférences de style',
  'icon_preference': 'Préférence icône/symbole',
  'typography_preference': 'Préférence typographique',
  'competitors': 'Concurrents à éviter',
  
  // App Mobile
  'app_platforms': 'Plateformes (iOS/Android)',
  'auth_methods': 'Méthodes d\'authentification',
  'integrations': 'Intégrations tierces',
  'offline_mode': 'Mode hors ligne',
  'push_notifications': 'Notifications push',
  
  // E-commerce
  'products_count': 'Nombre de produits',
  'payment_methods': 'Méthodes de paiement',
  'shipping_options': 'Options de livraison',
  'inventory_management': 'Gestion des stocks',
  
  // Marketing
  'campaign_type': 'Type de campagne',
  'campaign_budget': 'Budget publicitaire',
  'target_kpis': 'KPIs cibles',
  'campaign_duration': 'Durée de la campagne',
  
  // Budget et livrables
  'budget_estimate': 'Estimation Budget',
  'deliverables': 'Livrables agence',
  'client_elements': 'Éléments attendus du client',
  
  // Valeurs traduites
  'yes': 'Oui',
  'no': 'Non',
  'unknown': 'Je ne sais pas',
  'tbd': 'À définir',
  'leads': 'Générer des leads',
  'sales': 'Vendre en ligne',
  'visibility': 'Visibilité / Présence web',
  'information': 'Informer',
  'other': 'Autre'
};

// Fonction pour obtenir le label français
const getFieldLabelFr = (key) => {
  if (FIELD_LABELS_FR[key]) return FIELD_LABELS_FR[key];
  // Fallback: formater le key en label lisible
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Fonction pour traduire une valeur
const translateValue = (value) => {
  if (typeof value === 'string' && FIELD_LABELS_FR[value.toLowerCase()]) {
    return FIELD_LABELS_FR[value.toLowerCase()];
  }
  return value;
};

const BriefView = ({ brief, project, onSign, isAgency, isClient, theme: propTheme, wallet, onBriefUpdate }) => {
  const { theme: contextTheme } = useTheme();
  const theme = propTheme || contextTheme;
  const isBothSigned = brief.client_signed && brief.agency_signed;
  const canSign = (isAgency && !brief.agency_signed) || (isClient && !brief.client_signed);
  
  // State for client agreement
  const [hasAcceptedTerms, setHasAcceptedTerms] = React.useState(false);
  const [showSignatureModal, setShowSignatureModal] = React.useState(false);
  const [showWalletWarning, setShowWalletWarning] = React.useState(false);
  const [updatingContent, setUpdatingContent] = React.useState(null);

  // Calculate project budget
  const projectBudget = (project?.estimated_hours || 0) * (project?.hourly_rate || 75);
  // Use available_hours (balance minus reservations) instead of balance_hours
  const availableHours = wallet?.available_hours ?? wallet?.balance_hours ?? 0;
  const walletBalanceEuros = availableHours * (wallet?.hourly_rate || 75);
  const hasEnoughCredit = walletBalanceEuros >= projectBudget;
  
  // Can edit received content (agency/PM only, before full signature)
  const canEditContent = isAgency && !isBothSigned;

  // Handle toggle received content checkbox
  const handleToggleReceivedContent = async (contentId, currentValue) => {
    if (!canEditContent || !brief.id) return;
    
    setUpdatingContent(contentId);
    try {
      const newReceivedContent = {
        ...(brief.received_content || {}),
        [contentId]: !currentValue
      };
      
      await api.put(`/briefs/${brief.id}/received-content`, newReceivedContent);
      
      // Update local state via parent callback
      if (onBriefUpdate) {
        onBriefUpdate({
          ...brief,
          received_content: newReceivedContent
        });
      }
      
      toast.success(!currentValue ? 'Élément marqué comme reçu' : 'Élément décoché');
    } catch (error) {
      console.error('Error updating received content:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdatingContent(null);
    }
  };

  // Handle sign button click
  const handleSignClick = () => {
    if (isClient) {
      // Check if client has accepted terms
      if (!hasAcceptedTerms) {
        return;
      }
      // Check wallet balance
      if (!hasEnoughCredit && projectBudget > 0) {
        setShowWalletWarning(true);
        return;
      }
      setShowSignatureModal(true);
    } else {
      onSign();
    }
  };

  // Confirm signature
  const confirmSignature = () => {
    setShowSignatureModal(false);
    setHasAcceptedTerms(false);
    onSign();
  };

  // Format date for French locale
  const formatDateFR = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Group fields by section (based on label patterns)
  const groupedFields = brief.fields?.reduce((acc, field) => {
    // Simple grouping - in a real app, you'd have section info in the field
    const section = 'Détails du brief';
    if (!acc[section]) acc[section] = [];
    acc[section].push(field);
    return acc;
  }, {}) || {};

  const formatFieldValue = (field) => {
    if (field.field_type === 'checkbox') {
      return field.value ? '✓ Oui' : '✗ Non';
    }
    return field.value || '-';
  };

  // Télécharger le brief en PDF - VERSION PROFESSIONNELLE
  const downloadBriefPDF = async () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      
      // Couleurs de l'agence
      const primaryColor = theme?.colors?.primary || '#1a365d';
      const accentColor = theme?.colors?.accent || '#00d4aa';
      
      // Convertir hex en RGB
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 26, g: 54, b: 93 };
      };
      
      const primary = hexToRgb(primaryColor);
      const accent = hexToRgb(accentColor);
      
      // ===== EN-TÊTE PROFESSIONNEL =====
      // Bande de couleur en haut
      doc.setFillColor(primary.r, primary.g, primary.b);
      doc.rect(0, 0, pageWidth, 45, 'F');
      
      // Bande accent
      doc.setFillColor(accent.r, accent.g, accent.b);
      doc.rect(0, 45, pageWidth, 3, 'F');
      
      // Logo (si disponible, sinon texte)
      const logoUrl = theme?.logo?.lightUrl || theme?.logo?.darkUrl || theme?.logo?.url;
      if (logoUrl && logoUrl.startsWith('data:image')) {
        // Logo base64 - utiliser directement
        try {
          doc.addImage(logoUrl, 'PNG', margin, 8, 30, 30);
        } catch (e) {
          console.error('Error adding logo to PDF:', e);
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.text(theme?.agency?.name || theme?.agencyName || 'MSC', margin, 28);
        }
      } else if (logoUrl) {
        try {
          // Créer une image à partir du logo URL
          const img = new window.Image();
          img.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            setTimeout(reject, 3000); // Timeout après 3 secondes
            img.src = logoUrl;
          });
          doc.addImage(img, 'PNG', margin, 8, 30, 30);
        } catch (e) {
          // Si le logo ne charge pas, afficher le nom
          console.error('Error loading logo:', e);
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.text(theme?.agency?.name || theme?.agencyName || 'MSC', margin, 28);
        }
      } else {
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(theme?.agency?.name || theme?.agencyName || 'MSC', margin, 28);
      }
      
      // Titre du document
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('BRIEF PROJET', pageWidth - margin, 20, { align: 'right' });
      
      // Nom du projet
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(project?.name || 'Projet', pageWidth - margin, 32, { align: 'right' });
      
      // Date
      doc.setFontSize(9);
      doc.text(`Généré le ${formatDateFR(new Date().toISOString())}`, pageWidth - margin, 40, { align: 'right' });
      
      let yPos = 58;
      
      // ===== STATUT DU BRIEF =====
      // Encadré statut
      const statusHeight = 25;
      doc.setFillColor(isBothSigned ? 220 : 254, isBothSigned ? 252 : 243, isBothSigned ? 231 : 199);
      doc.roundedRect(margin, yPos, contentWidth, statusHeight, 3, 3, 'F');
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(isBothSigned ? 22 : 180, isBothSigned ? 163 : 83, isBothSigned ? 74 : 9);
      doc.text(`Statut: ${isBothSigned ? '✓ Brief validé et signé' : '⏳ En attente de signatures'}`, margin + 8, yPos + 10);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Agence: ${brief.agency_signed ? '✓ Signé' : '○ Non signé'}  |  Client: ${brief.client_signed ? '✓ Signé' : '○ Non signé'}`,
        margin + 8, yPos + 19
      );
      
      yPos += statusHeight + 15;
      
      // ===== INFORMATIONS CLIENT =====
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(margin, yPos, contentWidth / 2 - 5, 35, 3, 3, 'F');
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primary.r, primary.g, primary.b);
      doc.text('Client', margin + 8, yPos + 12);
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(9);
      doc.text(project?.client_name || 'Client non défini', margin + 8, yPos + 22);
      doc.text(project?.client_email || '', margin + 8, yPos + 30);
      
      // Info projet
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(margin + contentWidth / 2 + 5, yPos, contentWidth / 2 - 5, 35, 3, 3, 'F');
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primary.r, primary.g, primary.b);
      doc.text('Projet', margin + contentWidth / 2 + 13, yPos + 12);
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(9);
      doc.text(`Type: ${project?.project_type || brief.brief_type || 'Non défini'}`, margin + contentWidth / 2 + 13, yPos + 22);
      doc.text(`Créé le: ${formatDateFR(project?.created_at)}`, margin + contentWidth / 2 + 13, yPos + 30);
      
      yPos += 50;
      
      // ===== CONTENU DU BRIEF =====
      // Fonction pour ajouter une section
      const addSection = (title, yPosition) => {
        doc.setFillColor(primary.r, primary.g, primary.b);
        doc.rect(margin, yPosition, 4, 14, 'F');
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primary.r, primary.g, primary.b);
        doc.text(title, margin + 10, yPosition + 10);
        return yPosition + 20;
      };
      
      // Fonction pour ajouter une ligne de détail
      const addDetailLine = (label, value, yPosition, isLong = false) => {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 116, 139);
        doc.text(label, margin + 10, yPosition);
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 41, 59);
        
        if (isLong && value && value.length > 80) {
          const lines = doc.splitTextToSize(value, contentWidth - 20);
          lines.forEach((line, idx) => {
            doc.text(line, margin + 10, yPosition + 6 + (idx * 5));
          });
          return yPosition + 6 + (lines.length * 5) + 5;
        } else {
          doc.text(String(value || '-'), margin + 10, yPosition + 6);
          return yPosition + 15;
        }
      };
      
      // Si c'est un brief website avancé
      if (brief.brief_type === 'website_advanced') {
        // Section informations générales
        yPos = addSection('Informations générales', yPos);
        yPos = addDetailLine('Type de site', brief.site_type || '-', yPos);
        yPos = addDetailLine('Technologie', brief.technology || '-', yPos);
        yPos = addDetailLine('URL actuelle', brief.current_url || '-', yPos);
        
        if (brief.objective) {
          yPos = addDetailLine('Objectif principal', brief.objective, yPos, true);
        }
        if (brief.target_audience) {
          yPos = addDetailLine('Cible visée', brief.target_audience, yPos, true);
        }
        
        yPos += 5;
        
        // Arborescence
        if (brief.pages && brief.pages.length > 0) {
          yPos = addSection(`Arborescence (${brief.total_pages || brief.pages.length} pages)`, yPos);
          
          brief.pages.forEach(page => {
            if (yPos > pageHeight - 30) {
              doc.addPage();
              yPos = 20;
            }
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text(`• ${page.name}`, margin + 10, yPos);
            yPos += 6;
            
            if (page.subpages && page.subpages.length > 0) {
              doc.setFontSize(9);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(100, 116, 139);
              page.subpages.forEach(sp => {
                doc.text(`   └ ${sp.name}`, margin + 15, yPos);
                yPos += 5;
              });
            }
          });
          yPos += 10;
        }
        
        // Fonctionnalités
        if (brief.features && brief.features.length > 0) {
          const selectedFeatures = brief.features.filter(f => typeof f === 'string' || f?.selected !== false);
          yPos = addSection(`Fonctionnalités sélectionnées (${selectedFeatures.length})`, yPos);
          
          // Afficher en colonnes
          const cols = 2;
          const colWidth = (contentWidth - 20) / cols;
          let col = 0;
          let rowY = yPos;
          
          selectedFeatures.forEach((feature, idx) => {
            if (rowY > pageHeight - 30) {
              doc.addPage();
              rowY = 20;
              col = 0;
            }
            
            // Handle both string and object formats
            const featureName = typeof feature === 'string' ? feature : feature?.name || feature?.id || 'Feature';
            
            doc.setFillColor(accent.r, accent.g, accent.b);
            doc.circle(margin + 12 + (col * colWidth), rowY - 1.5, 1.5, 'F');
            
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(30, 41, 59);
            doc.text(featureName.substring(0, 35), margin + 18 + (col * colWidth), rowY);
            
            col++;
            if (col >= cols) {
              col = 0;
              rowY += 7;
            }
          });
          yPos = rowY + 15;
        }
        
        // Design
        yPos = addSection('Direction artistique', yPos);
        yPos = addDetailLine('Style souhaité', brief.design_style || '-', yPos);
        yPos = addDetailLine('Textes fournis', brief.has_texts ? 'Oui' : 'Non', yPos);
        yPos = addDetailLine('Images fournies', brief.has_images ? 'Oui' : 'Non', yPos);
        yPos = addDetailLine('Site multilingue', brief.is_multilingual ? `Oui (${brief.languages?.join(', ') || ''})` : 'Non', yPos);
        
      } else {
        // Brief classique
        yPos = addSection('Détails du brief', yPos);
        
        brief.fields?.forEach(field => {
          yPos = addDetailLine(
            field.label, 
            formatFieldValue(field), 
            yPos, 
            field.field_type === 'textarea'
          );
        });
      }
      
      // ===== PIED DE PAGE =====
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Ligne de séparation
        doc.setDrawColor(226, 232, 240);
        doc.line(margin, pageHeight - 18, pageWidth - margin, pageHeight - 18);
        
        // Infos agence
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text(theme?.agencyName || 'MSC Manager', margin, pageHeight - 10);
        
        // Numéro de page
        doc.text(`Page ${i}/${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        
        // Confidentialité
        doc.text('Document confidentiel', pageWidth / 2, pageHeight - 10, { align: 'center' });
      }
      
      // Télécharger
      doc.save(`Brief_${project?.name?.replace(/\s+/g, '_') || 'projet'}_${new Date().toLocaleDateString('fr-FR').replace(/\//g, '-')}.pdf`);
      toast.success('Brief PDF téléchargé !');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Brief Status Card */}
      <Card className="card">
        <CardContent className="p-3 sm:p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div 
                className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: isBothSigned ? '#dcfce7' : `${theme?.colors?.alert}20` }}
              >
                {isBothSigned ? (
                  <CheckCircle2 className="w-5 h-5 sm:w-7 sm:h-7 text-green-600" />
                ) : (
                  <Clock className="w-5 h-5 sm:w-7 sm:h-7" style={{ color: theme?.colors?.alert }} />
                )}
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg font-semibold" style={{ color: theme?.colors?.primary }}>
                  {isBothSigned ? 'Brief validé' : 'Brief en attente de signatures'}
                </h3>
                <p className="text-xs sm:text-sm text-[#64748b]">
                  {isBothSigned 
                    ? 'Le brief a été signé par les deux parties' 
                    : 'Le brief doit être signé par l\'agence et le client pour démarrer'
                  }
                </p>
              </div>
            </div>
            
            <div className="flex flex-row gap-2">
              <Button variant="outline" size="sm" onClick={downloadBriefPDF} className="flex-1 sm:flex-none text-xs sm:text-sm">
                <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden xs:inline">Télécharger </span>PDF
              </Button>
              {canSign && isClient && (
                <Button 
                  onClick={handleSignClick}
                  size="sm"
                  className="flex-1 sm:flex-none text-xs sm:text-sm"
                  style={{ backgroundColor: hasAcceptedTerms ? theme?.colors?.accent : '#94a3b8' }}
                  disabled={!hasAcceptedTerms}
                  title={!hasAcceptedTerms ? "Veuillez accepter les conditions" : ""}
                >
                  <PenTool className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  Signer
                </Button>
              )}
              {canSign && isAgency && (
                <Button 
                  onClick={handleSignClick}
                  size="sm"
                  className="flex-1 sm:flex-none text-xs sm:text-sm"
                  style={{ backgroundColor: theme?.colors?.accent }}
                >
                  <PenTool className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  Signer
                </Button>
              )}
            </div>
            
            {/* Wallet Warning Modal */}
            {showWalletWarning && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg" style={{ color: theme?.colors?.primary }}>
                        Crédit insuffisant
                      </h3>
                      <p className="text-sm text-slate-500">Rechargez votre wallet pour continuer</p>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600">Budget du projet</span>
                      <span className="font-semibold text-red-600">{projectBudget.toLocaleString('fr-FR')}€</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600">Votre crédit disponible</span>
                      <span className="font-semibold">{walletBalanceEuros.toLocaleString('fr-FR')}€</span>
                    </div>
                    <div className="border-t pt-2 mt-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Montant à recharger</span>
                        <span className="font-bold text-amber-600">{(projectBudget - walletBalanceEuros).toLocaleString('fr-FR')}€</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setShowWalletWarning(false)} className="flex-1">
                      Annuler
                    </Button>
                    <Button 
                      onClick={() => window.location.href = '/wallet'}
                      style={{ backgroundColor: theme?.colors?.accent }}
                      className="flex-1"
                    >
                      Recharger mon wallet
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Read confirmation modal for client */}
            {showSignatureModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <PenTool className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg" style={{ color: theme?.colors?.primary }}>
                        Confirmer la signature
                      </h3>
                      <p className="text-sm text-slate-500">Brief du projet : {project?.name}</p>
                    </div>
                  </div>
                  
                  {/* Budget summary */}
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600">Budget du projet</span>
                      <span className="font-semibold">{projectBudget.toLocaleString('fr-FR')}€</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Heures estimées</span>
                      <span className="font-semibold">{project?.estimated_hours || 0}h</span>
                    </div>
                  </div>
                  
                  <p className="text-slate-600 mb-4 text-sm">
                    En signant ce brief, vous confirmez avoir lu et accepté les termes du projet. 
                    Le budget sera réservé sur votre wallet.
                  </p>
                  <div className="flex gap-3 justify-end">
                    <Button variant="outline" onClick={() => setShowSignatureModal(false)}>
                      Annuler
                    </Button>
                    <Button 
                      onClick={confirmSignature}
                      style={{ backgroundColor: theme?.colors?.accent }}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Confirmer la signature
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Signatures Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t">
            <div className={cn(
              "p-2.5 sm:p-4 rounded-lg border-2",
              brief.agency_signed ? "border-green-200 bg-green-50" : "border-dashed border-[#e2e8f0]"
            )}>
              <div className="flex items-center gap-2 mb-1 sm:mb-2">
                {brief.agency_signed ? (
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#64748b] shrink-0" />
                )}
                <span className="font-medium text-xs sm:text-base" style={{ color: theme?.colors?.primary }}>
                  Signature Agence
                </span>
              </div>
              {brief.agency_signed ? (
                <p className="text-xs sm:text-sm text-green-600">
                  Signé le {new Date(brief.agency_signature_date).toLocaleDateString('fr-FR')}
                </p>
              ) : (
                <p className="text-xs sm:text-sm text-[#64748b]">En attente</p>
              )}
            </div>

            <div className={cn(
              "p-2.5 sm:p-4 rounded-lg border-2",
              brief.client_signed ? "border-green-200 bg-green-50" : "border-dashed border-[#e2e8f0]"
            )}>
              <div className="flex items-center gap-2 mb-1 sm:mb-2">
                {brief.client_signed ? (
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#64748b] shrink-0" />
                )}
                <span className="font-medium text-xs sm:text-base" style={{ color: theme?.colors?.primary }}>
                  Signature Client
                </span>
              </div>
              {brief.client_signed ? (
                <p className="text-xs sm:text-sm text-green-600">
                  Signé le {new Date(brief.client_signature_date).toLocaleDateString('fr-FR')}
                </p>
              ) : (
                <p className="text-xs sm:text-sm text-[#64748b]">En attente</p>
              )}
            </div>
          </div>

        </CardContent>
      </Card>

      {/* Budget Section - Only show after both signed */}
      {isBothSigned && (
        <Card className="card border-2" style={{ borderColor: theme?.colors?.accent }}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#64748b] mb-1">Budget validé</p>
                <p className="text-3xl font-mono font-bold" style={{ color: theme?.colors?.primary }}>
                  {((project?.estimated_hours || 0) * (project?.hourly_rate || 75)).toLocaleString('fr-FR')}€
                </p>
                <p className="text-sm text-[#64748b]">
                  {project?.estimated_hours || 0} heures × {project?.hourly_rate || 75}€/h
                </p>
              </div>
              <div 
                className="w-16 h-16 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${theme?.colors?.accent}20` }}
              >
                <FileText className="w-8 h-8" style={{ color: theme?.colors?.accent }} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Brief Content */}
      <Card className="card">
        <CardHeader className="p-3 sm:p-6 pb-2 sm:pb-4">
          <CardTitle className="text-base sm:text-lg" style={{ color: theme?.colors?.primary }}>
            Cahier des charges
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Créé le {new Date(brief.created_at).toLocaleDateString('fr-FR')}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0">
          {/* Vue spéciale pour brief website avancé OU tout brief avec dynamic_fields */}
          {(brief.brief_type === 'website_advanced' || brief.dynamic_fields || brief.site_type) ? (
            <div className="space-y-4 sm:space-y-6">
              {/* Informations générales */}
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                {brief.technology && (
                <div className="p-2 sm:p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-0.5 sm:mb-1">Technologie</p>
                  <p className="font-semibold text-xs sm:text-base" style={{ color: theme?.colors?.primary }}>
                    {brief.technology === 'wordpress' ? 'WordPress' : 
                     brief.technology === 'react' ? 'React' :
                     brief.technology === 'shopify' ? 'Shopify' : 'Sur mesure'}
                  </p>
                </div>
                )}
                <div className="p-2 sm:p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-0.5 sm:mb-1">Type de projet</p>
                  <p className="font-semibold text-xs sm:text-base" style={{ color: theme?.colors?.primary }}>
                    {brief.site_type || brief.brief_type || project?.project_type || '-'}
                  </p>
                </div>
              </div>

              {brief.objective && (
                <div>
                  <p className="text-xs text-slate-500 mb-0.5 sm:mb-1">Objectif / Description</p>
                  <p className="text-xs sm:text-base text-slate-700 whitespace-pre-wrap">{brief.objective || brief.description}</p>
                </div>
              )}

              {/* Audio Brief - Show if audio_url exists OR transcript exists */}
              {(brief.audio_url || brief.audio_transcript) && (
                <div className="p-3 sm:p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">🎙️</span>
                    <p className="text-xs sm:text-sm font-medium text-indigo-700">Brief audio</p>
                  </div>
                  {brief.audio_url ? (
                    <audio 
                      src={brief.audio_url} 
                      controls 
                      className="w-full h-10"
                      controlsList="nodownload"
                    />
                  ) : (
                    <p className="text-xs text-amber-600 italic mb-2">
                      (Fichier audio non disponible - seule la transcription est affichée)
                    </p>
                  )}
                  {brief.audio_transcript && (
                    <div className={cn("p-2 bg-white rounded border border-indigo-100", brief.audio_url && "mt-3")}>
                      <p className="text-xs text-slate-500 mb-1">Transcription :</p>
                      <p className="text-xs sm:text-sm text-slate-700 italic whitespace-pre-wrap">
                        "{brief.audio_transcript}"
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Liens d'inspiration */}
              {brief.inspiration_links && brief.inspiration_links.length > 0 && brief.inspiration_links.some(l => l) && (
                <div>
                  <p className="text-xs text-slate-500 mb-0.5 sm:mb-1">Liens d'inspiration</p>
                  <div className="flex flex-wrap gap-2">
                    {brief.inspiration_links.filter(l => l).map((link, idx) => (
                      <a 
                        key={idx}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                      >
                        🔗 {new URL(link).hostname}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Champs dynamiques du formulaire */}
              {brief.dynamic_fields && Object.keys(brief.dynamic_fields).length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-xs sm:text-base border-b pb-2" style={{ color: theme?.colors?.primary }}>
                    Détails du brief
                  </h4>
                  {Object.entries(brief.dynamic_fields).map(([key, value]) => {
                    if (!value || key === 'description' || key === 'project_type') return null;
                    
                    // Use French label from dictionary
                    const label = getFieldLabelFr(key);
                    
                    // Handle different value types
                    let displayValue;
                    
                    // Special handling for client_elements - display as checklist
                    if (key === 'client_elements' && Array.isArray(value)) {
                      if (value.length === 0) return null;
                      displayValue = (
                        <div className="space-y-2">
                          {value.map((item, i) => {
                            const itemLabel = item.label || item.name || item;
                            const isReceived = brief.received_content?.[`client_element_${i}`];
                            return (
                              <div 
                                key={i}
                                onClick={() => canEditContent && handleToggleReceivedContent(`client_element_${i}`, isReceived)}
                                className={cn(
                                  "flex items-center justify-between p-2 rounded-lg border transition-all",
                                  isReceived ? "bg-green-50 border-green-200" : "bg-white border-slate-200",
                                  canEditContent && "cursor-pointer hover:shadow-sm"
                                )}
                              >
                                <div className="flex items-center gap-2">
                                  {canEditContent && (
                                    <Checkbox 
                                      checked={!!isReceived}
                                      className="data-[state=checked]:bg-green-500"
                                      onCheckedChange={() => handleToggleReceivedContent(`client_element_${i}`, isReceived)}
                                    />
                                  )}
                                  <span className="text-sm">{typeof itemLabel === 'string' ? itemLabel : itemLabel}</span>
                                </div>
                                <Badge className={cn(
                                  "text-xs",
                                  isReceived ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                                )}>
                                  {isReceived ? '✓ Reçu' : 'En attente'}
                                </Badge>
                              </div>
                            );
                          })}
                        </div>
                      );
                    } else if (Array.isArray(value)) {
                      if (value.length === 0) return null;
                      // Check if array of objects with label/name
                      if (typeof value[0] === 'object' && (value[0].label || value[0].name)) {
                        displayValue = (
                          <div className="flex flex-wrap gap-1">
                            {value.map((item, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {item.label || item.name || item.value || JSON.stringify(item)}
                              </Badge>
                            ))}
                          </div>
                        );
                      } else {
                        displayValue = (
                          <div className="flex flex-wrap gap-1">
                            {value.map((item, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {typeof item === 'string' ? translateValue(item) : JSON.stringify(item)}
                              </Badge>
                            ))}
                          </div>
                        );
                      }
                    } else if (typeof value === 'object' && value !== null) {
                      // Skip budget_estimate object rendering (it's internal)
                      if (key === 'budget_estimate') return null;
                      
                      // Object - render as key-value pairs with French labels
                      displayValue = (
                        <div className="bg-slate-50 rounded p-2 text-xs space-y-1">
                          {Object.entries(value).map(([k, v]) => (
                            <div key={k} className="flex justify-between">
                              <span className="text-slate-500">{getFieldLabelFr(k)}:</span>
                              <span className="font-medium">{translateValue(String(v))}</span>
                            </div>
                          ))}
                        </div>
                      );
                    } else if (typeof value === 'boolean') {
                      displayValue = (
                        <Badge className={value ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                          {value ? 'Oui' : 'Non'}
                        </Badge>
                      );
                    } else {
                      // Translate simple string values
                      displayValue = <p className="text-xs sm:text-sm text-slate-700 whitespace-pre-wrap">{translateValue(String(value))}</p>;
                    }
                    
                    return (
                      <div key={key} className="p-2 sm:p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500 mb-0.5 sm:mb-1">{label}</p>
                        {displayValue}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Arborescence */}
              {brief.pages && brief.pages.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-base" style={{ color: theme?.colors?.primary }}>
                    <Folder className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                    Arborescence ({brief.total_pages || brief.pages.length} pages)
                  </h4>
                  <div className="border rounded-lg divide-y overflow-hidden">
                    {brief.pages.map((page, idx) => (
                      <div key={idx} className="p-1.5 sm:p-3">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <Folder className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-500 shrink-0" />
                          <span className="font-medium text-xs sm:text-base truncate">{page.name}</span>
                        </div>
                        {page.subpages && page.subpages.length > 0 && (
                          <div className="ml-4 sm:ml-6 mt-1 sm:mt-2 space-y-0.5 sm:space-y-1">
                            {page.subpages.map((sp, spIdx) => (
                              <div key={spIdx} className="flex items-center gap-1.5 sm:gap-2 text-xs text-slate-600">
                                <File className="w-3 h-3 shrink-0" />
                                <span className="truncate">{sp.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fonctionnalités */}
              {brief.features && brief.features.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-base" style={{ color: theme?.colors?.primary }}>
                    <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                    Fonctionnalités ({brief.features.filter(f => typeof f === 'string' || f?.selected).length})
                  </h4>
                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {brief.features.map((feature, idx) => {
                      // Handle both string and object formats
                      const featureName = typeof feature === 'string' ? feature : feature?.name || feature?.id || 'Feature';
                      const isSelected = typeof feature === 'string' ? true : feature?.selected !== false;
                      if (!isSelected) return null;
                      return (
                        <Badge key={idx} variant="secondary" className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 sm:px-2 sm:py-1">
                          {featureName}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Contenus à fournir */}
              {brief.content_requirements && brief.content_requirements.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-base" style={{ color: theme?.colors?.primary }}>
                    <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                    Contenus à fournir
                    {canEditContent && (
                      <span className="text-xs font-normal text-slate-500 ml-2">(cliquez pour cocher)</span>
                    )}
                  </h4>
                  <div className="space-y-1.5 sm:space-y-2">
                    {brief.content_requirements.map((content, idx) => {
                      const received = brief.received_content?.[content.id];
                      const isUpdating = updatingContent === content.id;
                      return (
                        <div 
                          key={idx}
                          onClick={() => canEditContent && handleToggleReceivedContent(content.id, received)}
                          className={cn(
                            "flex items-center justify-between p-1.5 sm:p-3 rounded-lg border gap-2 transition-all",
                            received ? "bg-green-50 border-green-200" : "bg-white",
                            canEditContent && "cursor-pointer hover:shadow-sm hover:border-slate-300"
                          )}
                        >
                          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                            {canEditContent && (
                              <div className="shrink-0">
                                {isUpdating ? (
                                  <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                                ) : (
                                  <Checkbox 
                                    checked={!!received}
                                    className={cn(
                                      "data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                    onCheckedChange={() => handleToggleReceivedContent(content.id, received)}
                                  />
                                )}
                              </div>
                            )}
                            {content.type === 'image' ? (
                              <Image className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-500 shrink-0" />
                            ) : (
                              <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-500 shrink-0" />
                            )}
                            <span className="text-xs truncate">{content.label}</span>
                          </div>
                          <Badge className={cn(
                            "shrink-0 text-xs px-1.5 py-0.5",
                            received ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"
                          )}>
                            {received ? '✓ Reçu' : 'En attente'}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Barre de progression */}
                  <div className="mt-3 sm:mt-4 p-2 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between text-xs mb-1.5 sm:mb-2">
                      <span className="text-slate-600">Progression</span>
                      <span className="font-medium">
                        {Object.values(brief.received_content || {}).filter(Boolean).length} / {brief.content_requirements.length}
                      </span>
                    </div>
                    <div className="h-1.5 sm:h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 transition-all"
                        style={{ 
                          width: `${(Object.values(brief.received_content || {}).filter(Boolean).length / brief.content_requirements.length) * 100}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Vue classique pour les autres types de brief */
            <div className="space-y-6">
              {brief.fields?.map((field, index) => (
                <div key={index} className="border-b border-[#e2e8f0] pb-4 last:border-0">
                  <label className="text-sm font-medium text-[#64748b] block mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <div className="mt-1">
                    {field.field_type === 'checkbox' ? (
                      <Badge className={field.value ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                        {field.value ? 'Oui' : 'Non'}
                      </Badge>
                    ) : field.field_type === 'textarea' ? (
                      <p className="text-[#1a365d] whitespace-pre-wrap">
                        {field.value || <span className="text-[#94a3b8] italic">Non renseigné</span>}
                      </p>
                    ) : (
                      <p className="font-medium" style={{ color: theme?.colors?.primary }}>
                        {field.value || <span className="text-[#94a3b8] italic">Non renseigné</span>}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Agreement Checkbox - AFTER the brief content, only show for client when not yet signed */}
      {isClient && !brief.client_signed && (
        <Card className="card border-2 border-amber-200 bg-amber-50/50">
          <CardContent className="p-4 sm:p-6">
            {/* Budget Info for Client */}
            <div className="bg-white rounded-lg p-3 sm:p-4 mb-4 shadow-sm">
              <h4 className="font-semibold text-sm sm:text-base mb-3" style={{ color: theme?.colors?.primary }}>
                Résumé du budget
              </h4>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <span className="text-slate-500 text-xs">Budget du projet</span>
                  <p className="font-bold text-base sm:text-xl" style={{ color: theme?.colors?.primary }}>
                    {projectBudget.toLocaleString('fr-FR')}€
                  </p>
                </div>
                <div>
                  <span className="text-slate-500 text-xs">Crédit disponible</span>
                  <p className={cn("font-bold text-base sm:text-xl", hasEnoughCredit ? "text-green-600" : "text-red-600")}>
                    {walletBalanceEuros.toLocaleString('fr-FR')}€
                  </p>
                </div>
              </div>
              {!hasEnoughCredit && projectBudget > 0 && (
                <div className="mt-3 p-2.5 bg-amber-100 rounded-lg">
                  <p className="text-xs text-amber-800 flex items-start gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      Crédit insuffisant. Rechargez de <strong>{(projectBudget - walletBalanceEuros).toLocaleString('fr-FR')}€</strong>.
                      {' '}
                      <a 
                        href="/wallet" 
                        className="underline font-semibold hover:text-amber-900"
                      >
                        Recharger →
                      </a>
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* Agreement Checkbox */}
            <label className={cn(
              "flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all mb-4",
              hasAcceptedTerms 
                ? "border-green-400 bg-green-50" 
                : "border-slate-300 hover:border-slate-400 bg-white"
            )}>
              <input
                type="checkbox"
                checked={hasAcceptedTerms}
                onChange={(e) => setHasAcceptedTerms(e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded border-slate-300 text-green-600 focus:ring-green-500"
              />
              <div>
                <p className="font-medium text-sm sm:text-base" style={{ color: theme?.colors?.primary }}>
                  Je reconnais avoir lu le brief dans sa totalité et j&apos;accepte les conditions
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  En cochant cette case, je confirme avoir pris connaissance de l&apos;ensemble des spécifications du projet, 
                  du budget estimé ({projectBudget.toLocaleString('fr-FR')}€ pour {project?.estimated_hours || 0} heures), 
                  et j&apos;accepte les conditions de réalisation du projet.
                </p>
              </div>
            </label>

            {/* Sign Button */}
            <Button 
              onClick={handleSignClick}
              size="lg"
              className="w-full text-base py-6"
              style={{ backgroundColor: hasAcceptedTerms ? theme?.colors?.accent : '#94a3b8' }}
              disabled={!hasAcceptedTerms}
            >
              <PenTool className="w-5 h-5 mr-2" />
              Signer le brief
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BriefView;
